import React, { useMemo, useState } from "react";
import { ArrowDownUp, ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "../../lib/utils";
import LoadingSkeleton from "./LoadingSkeleton";
import EmptyState from "./EmptyState";
import ErrorState from "./ErrorState";
import FilterBar from "./FilterBar";

/**
 * DataTable — unified, RTL-aware enterprise table shell.
 *
 * Supports client search, filters (via FilterBar slot), sorting, loading/error/
 * empty states, optional row selection, quick row actions, sticky header and
 * financial/tabular numeric columns without changing existing page APIs.
 */
const DataTable = ({
  columns = [],
  rows = [],
  loading = false,
  error = null,
  emptyTitle = "لا توجد بيانات",
  emptyDescription = "",
  emptyIcon = null,
  emptyAction = null,
  onRowClick = null,
  rowKey = (r, i) => r?.id ?? i,
  dense = false,
  stickyHeader = true,
  className = "",
  footer = null,
  searchable = false,
  searchPlaceholder = "ابحث في الجدول...",
  searchValue,
  onSearchChange,
  searchKeys = [],
  filters = null,
  toolbarActions = null,
  selectable = false,
  selectedRows,
  onSelectedRowsChange,
  rowActions = null,
  defaultSort = null,
  caption = "جدول بيانات",
}) => {
  const [internalSearch, setInternalSearch] = useState("");
  const [internalSelected, setInternalSelected] = useState([]);
  const [sort, setSort] = useState(defaultSort);

  const resolvedSearch = searchValue ?? internalSearch;
  const selected = selectedRows ?? internalSelected;
  const setSelected = (next) => {
    if (onSelectedRowsChange) onSelectedRowsChange(next);
    else setInternalSelected(next);
  };

  const alignClass = (a, financial) =>
    financial || a === "money" || a === "end"
      ? "text-end"
      : a === "center"
        ? "text-center"
        : "text-start";

  const cellPad = dense ? "px-3 py-2.5" : "px-4 py-3";

  const visibleRows = useMemo(() => {
    let next = Array.isArray(rows) ? [...rows] : [];
    const q = String(resolvedSearch || "")
      .trim()
      .toLowerCase();
    if (searchable && q) {
      next = next.filter((row) => {
        const keys = searchKeys.length ? searchKeys : columns.map((c) => c.key);
        return keys.some((key) =>
          String(row?.[key] ?? "")
            .toLowerCase()
            .includes(q),
        );
      });
    }
    if (sort?.key) {
      const col = columns.find((c) => c.key === sort.key);
      next.sort((a, b) => {
        const av = col?.sortValue ? col.sortValue(a) : a?.[sort.key];
        const bv = col?.sortValue ? col.sortValue(b) : b?.[sort.key];
        if (av === bv) return 0;
        if (av === null || av === undefined) return 1;
        if (bv === null || bv === undefined) return -1;
        const cmp =
          typeof av === "number" && typeof bv === "number"
            ? av - bv
            : String(av).localeCompare(String(bv), "ar", { numeric: true });
        return sort.dir === "desc" ? -cmp : cmp;
      });
    }
    return next;
  }, [columns, rows, resolvedSearch, searchable, searchKeys, sort]);

  const allVisibleKeys = visibleRows.map((row, index) => rowKey(row, index));
  const allSelected =
    allVisibleKeys.length > 0 &&
    allVisibleKeys.every((key) => selected.includes(key));
  const toggleAll = () =>
    setSelected(
      allSelected
        ? selected.filter((key) => !allVisibleKeys.includes(key))
        : Array.from(new Set([...selected, ...allVisibleKeys])),
    );
  const toggleOne = (key) =>
    setSelected(
      selected.includes(key)
        ? selected.filter((id) => id !== key)
        : [...selected, key],
    );

  const handleSearch = (value) => {
    if (onSearchChange) onSearchChange(value);
    else setInternalSearch(value);
  };

  const toggleSort = (col) => {
    if (col.sortable === false) return;
    setSort((prev) => {
      if (prev?.key !== col.key) return { key: col.key, dir: "asc" };
      if (prev.dir === "asc") return { key: col.key, dir: "desc" };
      return null;
    });
  };

  const hasToolbar = searchable || filters || toolbarActions;

  return (
    <div className={cn("space-y-3", className)}>
      {hasToolbar && (
        <FilterBar
          searchValue={resolvedSearch}
          onSearchChange={handleSearch}
          onClear={() => handleSearch("")}
          searchPlaceholder={searchPlaceholder}
          filters={filters}
          actions={toolbarActions}
          showSearch={searchable}
        />
      )}
      <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-900">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm" aria-busy={loading}>
            <caption className="sr-only">{caption}</caption>
            <thead
              className={cn(
                "bg-neutral-50 text-[11px] font-semibold uppercase tracking-wide text-neutral-500 dark:bg-neutral-900/60 dark:text-neutral-400",
                stickyHeader && "sticky top-0 z-[1]",
              )}
            >
              <tr>
                {selectable && (
                  <th
                    scope="col"
                    className="w-10 border-b border-neutral-200 px-3 py-3 dark:border-neutral-800"
                  >
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      aria-label="تحديد كل الصفوف الظاهرة"
                      className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                    />
                  </th>
                )}
                {columns.map((col) => {
                  const activeSort = sort?.key === col.key;
                  const SortIcon = !activeSort
                    ? ArrowDownUp
                    : sort.dir === "asc"
                      ? ChevronUp
                      : ChevronDown;
                  return (
                    <th
                      key={col.key}
                      scope="col"
                      className={cn(
                        "whitespace-nowrap border-b border-neutral-200 px-4 py-3 dark:border-neutral-800",
                        alignClass(col.align, col.financial),
                        col.headerClassName,
                      )}
                      style={col.width ? { width: col.width } : undefined}
                      aria-sort={
                        col.sortable === false
                          ? undefined
                          : activeSort
                            ? sort.dir === "asc"
                              ? "ascending"
                              : "descending"
                            : "none"
                      }
                    >
                      {col.sortable === false ? (
                        col.label
                      ) : (
                        <button
                          type="button"
                          onClick={() => toggleSort(col)}
                          className={cn(
                            "inline-flex items-center gap-1 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500/40",
                            alignClass(col.align, col.financial) ===
                              "text-end" && "flex-row-reverse",
                          )}
                          aria-label={`فرز حسب ${col.label}`}
                        >
                          <span>{col.label}</span>
                          <SortIcon size={12} aria-hidden="true" />
                        </button>
                      )}
                    </th>
                  );
                })}
                {rowActions && (
                  <th
                    scope="col"
                    className="w-24 border-b border-neutral-200 px-4 py-3 text-center dark:border-neutral-800"
                  >
                    إجراءات
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {loading && visibleRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={
                      columns.length +
                      (selectable ? 1 : 0) +
                      (rowActions ? 1 : 0)
                    }
                    className="p-5"
                  >
                    <LoadingSkeleton variant="table" count={5} />
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td
                    colSpan={
                      columns.length +
                      (selectable ? 1 : 0) +
                      (rowActions ? 1 : 0)
                    }
                    className="p-5"
                  >
                    <ErrorState error={error} />
                  </td>
                </tr>
              ) : visibleRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={
                      columns.length +
                      (selectable ? 1 : 0) +
                      (rowActions ? 1 : 0)
                    }
                    className="p-5"
                  >
                    <EmptyState
                      icon={emptyIcon}
                      title={emptyTitle}
                      description={emptyDescription}
                      actionLabel={emptyAction?.label}
                      onAction={emptyAction?.onClick}
                    />
                  </td>
                </tr>
              ) : (
                visibleRows.map((row, idx) => {
                  const key = rowKey(row, idx);
                  const isSelected = selected.includes(key);
                  return (
                    <tr
                      key={key}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                      className={cn(
                        "border-b border-neutral-100 text-neutral-700 transition-colors duration-150 last:border-b-0 hover:bg-neutral-50 dark:border-neutral-800/70 dark:text-neutral-200 dark:hover:bg-neutral-800/60",
                        onRowClick && "cursor-pointer",
                        isSelected && "bg-primary-50/70 dark:bg-primary-950/20",
                      )}
                    >
                      {selectable && (
                        <td
                          className="px-3 py-3"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleOne(key)}
                            aria-label="تحديد الصف"
                            className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                          />
                        </td>
                      )}
                      {columns.map((col) => {
                        const value = col.render
                          ? col.render(row, idx)
                          : row?.[col.key];
                        return (
                          <td
                            key={col.key}
                            className={cn(
                              cellPad,
                              alignClass(col.align, col.financial),
                              col.className,
                              (col.mono || col.financial) &&
                                "font-mono tabular-nums",
                            )}
                            style={col.width ? { width: col.width } : undefined}
                          >
                            {value ?? "—"}
                          </td>
                        );
                      })}
                      {rowActions && (
                        <td
                          className="px-4 py-3 text-center"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {rowActions(row, idx)}
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {footer && (
          <div className="border-t border-neutral-100 bg-neutral-50 px-4 py-3 text-xs text-neutral-500 dark:border-neutral-800 dark:bg-neutral-900/50 dark:text-neutral-400">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default DataTable;
